<template>
  <vz-autocomplete
    ref="autocomplete"
    value-key="guid"
    value-template="{address}"
    title-template="{address}"
    placeholder="Введите улицу для поиска"
    icon-variant="primary"
    icon-url="/svg/vz-hand-cargo.svg"
    hide-no-data-text
    :label="$attrs.label || 'Пункт выдачи'"
    :items="items"
    :use-spinner="useSpinner"
    :modelValue="modelValue"
    @blur="onBlur"
    @search="search"
    v-bind="$attrs"
  >
    <div v-if="useMap" class="vz-direction-map-btn" @click="showMapModal">
      <vz-icon
        name="map-marker"
        variant="primary"
        :size="16"
      />
      <span class="fs-16" :class="{'color-inactive': $attrs.disabled}">На карте</span>
    </div>
    <slot />
  </vz-autocomplete>

  <client-only v-if="useMap">
    <vz-map-select-modal
      ref="mapSelectModal"
      :location="location"
      :pickup-point-guid="modelValue"
      :hide-switcher="!showSwitcher"
      :terminal-purpose="terminalPurpose"
      direction-type="pickupPoint"
      @submit="onMapSubmit"
    />
  </client-only>
</template>

<script setup lang="ts">
import {Location, PickupPoint, TerminalPurpose} from '~/types/types'
import {PropType} from 'vue'
import VzMapSelectModal from '~/components/modals/vz-map-select-modal.vue';
import VzAutocomplete from '~/components/ui-kit/vz-autocomplete.vue';
import {getPickupPoint, getPickupPointList} from '~/api/order';
import VzIcon from '~/components/ui-kit/vz-icon.vue';

// Условно наследуем компонент от VZAutocomplete
defineOptions({
  inheritAttrs: false
})

const emit = defineEmits(['map-submit'])

const props = defineProps({
  location: {
    type: Object as PropType<Location>,
    default: () => {}
  },
  modelValue: {
    type: String,
    default: ''
  },
  useMap: {
    type: Boolean,
    default: false
  },
  showSwitcher: {
    type: Boolean,
    default: false
  },
  suggestionOff: {
    type: Boolean,
    default: false
  },
  terminalPurpose: {
    type: String as PropType<''|TerminalPurpose>,
    default: ''
  },
})
const mapSelectModal = ref<typeof VzMapSelectModal>()
const autocomplete = ref()

const items = ref<PickupPoint[]>([])
const selected = ref<PickupPoint>()
const changeAfterSearch = ref(false)
const useSpinner = ref(false)

async function search(search: string): Promise<any> {
  useSpinner.value = true
  changeAfterSearch.value = false

  if (props.location) {
    // items.value = await getPickupPointList(search, props.location, { suggestionOff: props.suggestionOff })
    items.value = (await getPickupPointList(search, props.location)).data
    if (changeAfterSearch.value && search) {
      await nextTick()
      autocomplete.value?.onSelect()
    }
  }
  useSpinner.value = false
}

function onBlur() {
  changeAfterSearch.value = true
}

function showMapModal() {
  autocomplete.value?.close()
  mapSelectModal.value?.show()
}

function onMapSubmit(data: any) {
  emit('map-submit', data)
}

watch(() => props.location,
  () => {
    changeAfterSearch.value = false
    search('')
  }
)

watch(() => props.modelValue,
  async () => {
    if (props.modelValue) {
      selected.value = await getPickupPoint(props.modelValue)
    } else {
      selected.value = undefined
    }
    // Если список пустой, или там нет выбранного варианта
    if (!items.value.length || (selected.value && !items.value.find(item => item.guid === selected.value?.guid))) {
      await search(props.modelValue ? selected.value?.name || '' : '')
      if (selected.value && !items.value.find(item => item.guid === selected.value?.guid)) {
        items.value = [
          selected.value,
          ...items.value
        ]
      }
    }
  },
)

onMounted(async () => {
  if (props.modelValue) {
    selected.value = await getPickupPoint(props.modelValue);
    await search(selected.value?.name)
  }
})

defineExpose({
  selected,
  search
})
</script>

<style scoped>

</style>